import { CellValue } from '@/shared/ui/data/EditableGrid/types/cell.types';
import { Area, Bound, Position } from '@/shared/ui/data/EditableGrid/types/core.type';
import {
  createPosition,
  isInBounds,
  isSamePosition,
} from '@/shared/ui/data/EditableGrid/lib/position';
import { clipboardManager } from '@/shared/lib/clipboard';

export interface GridClipboardData {
  readonly cells: ReadonlyArray<ReadonlyArray<CellValue>>;
  readonly rowCount: number;
  readonly colCount: number;
}

export function saveClipboardToStorage(data: GridClipboardData): void {
  clipboardManager.write(data);
}

export function loadClipboardFromStorage(): GridClipboardData | null {
  const stored = clipboardManager.read();
  return stored ? (stored.data as GridClipboardData) : null;
}

export function cellsToTSV(cells: ReadonlyArray<ReadonlyArray<CellValue>>): string {
  return cells
    .map((row) =>
      row
        .map((cell) => {
          const isEmpty = cell === null || cell === undefined || cell === '';
          return isEmpty ? '' : String(cell);
        })
        .join('\t')
    )
    .join('\n');
}

export function tsvToCells(tsv: string): ReadonlyArray<ReadonlyArray<CellValue>> {
  const lines = tsv.split('\n');
  return lines.map((line) => line.split('\t'));
}

export function createClipboardData(
  positions: ReadonlyArray<Position>,
  getCellValue: (position: Position) => CellValue
): GridClipboardData {
  if (positions.length === 0) return { cells: [], rowCount: 0, colCount: 0 };

  const { minRow, minCol, rowCount, colCount } = calculateGridBound(positions);

  const cells: CellValue[][] = [];
  for (let r = 0; r < rowCount; r++) {
    const row: CellValue[] = [];
    for (let c = 0; c < colCount; c++) {
      const position = createPosition(minRow + r, minCol + c);

      const isSelected = positions.some((p) => isSamePosition(p, position));
      row.push(isSelected ? getCellValue(position) : null);
    }
    cells.push(row);
  }

  return { cells, rowCount, colCount };
}

function calculateGridBound(positions: ReadonlyArray<Position>): Area {
  const minRow = Math.min(...positions.map((p) => p.rowIndex));
  const maxRow = Math.max(...positions.map((p) => p.rowIndex));
  const minCol = Math.min(...positions.map((p) => p.colIndex));
  const maxCol = Math.max(...positions.map((p) => p.colIndex));

  const rowCount = maxRow - minRow + 1;
  const colCount = maxCol - minCol + 1;

  return { minRow, minCol, rowCount, colCount };
}

export function calculatePastePositions(
  startPosition: Position,
  clipboardData: GridClipboardData,
  bound: Bound,
  getColumn?: (colIndex: number) => any
): ReadonlyArray<{
  readonly position: Position;
  readonly value: CellValue;
}> {
  const result: Array<{ position: Position; value: CellValue }> = [];

  for (let r = 0; r < clipboardData.rowCount; r++) {
    for (let c = 0; c < clipboardData.colCount; c++) {
      const targetRow = startPosition.rowIndex + r;
      const targetCol = startPosition.colIndex + c;

      const position = createPosition(targetRow, targetCol);

      if (isInBounds(position, bound)) {
        const value = clipboardData.cells[r][c] ?? null;
        result.push({ position, value });
      }
    }
  }

  return result;
}

async function writeToClipboardAPI(tsv: string): Promise<boolean> {
  if (!navigator.clipboard?.writeText) return false;

  try {
    await navigator.clipboard.writeText(tsv);
    return true;
  } catch {
    return false;
  }
}

function writeToClipboardExecCommand(tsv: string): boolean {
  try {
    const textarea = document.createElement('textarea');
    textarea.value = tsv;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    const success = document.execCommand('copy');
    document.body.removeChild(textarea);
    return success;
  } catch {
    return false;
  }
}

export async function copyToClipboard(clipboardData: GridClipboardData): Promise<void> {
  const tsv = cellsToTSV(clipboardData.cells);

  const success = (await writeToClipboardAPI(tsv)) || writeToClipboardExecCommand(tsv);
  if (!success) throw new Error('Failed to copy to clipboard');
}

async function readFromClipboardAPI(): Promise<GridClipboardData | null> {
  if (!navigator.clipboard?.readText) return null;

  try {
    const tsv = await navigator.clipboard.readText();
    if (!tsv || tsv.trim() === '') return null;

    const cells = tsvToCells(tsv);
    return {
      cells,
      rowCount: cells.length,
      colCount: cells[0]?.length ?? 0,
    };
  } catch (err) {
    if (err instanceof Error && err.name === 'NotAllowedError') {
      return null;
    }
    return null;
  }
}

export async function pasteFromClipboard(): Promise<GridClipboardData> {
  const data = await readFromClipboardAPI();
  if (data) return data;

  throw new Error('Clipboard read failed');
}
